import React, { useEffect, useState } from 'react';
import {
  FiCheck,
  FiGlobe,
  FiLogOut,
  FiMail,
  FiPlayCircle,
  FiShield,
} from 'react-icons/fi';
import { type Language, useI18n } from '../../i18n';
import { authService } from '../../services/authService';
import { languagePreferenceService } from '../../services/languagePreferenceService';
import './ProfilePage.css';
import type { TourDefinition, TourId } from '../../tours/tourDefinitions';

interface ProfilePageProps {
  tours?: TourDefinition[];
  onReplayTour?: (tourId: TourId) => void;
}

const languages: Array<{
  value: Language;
  nativeLabel: string;
  region: string;
}> = [
  { value: 'en', nativeLabel: 'English', region: 'United Kingdom' },
  { value: 'fr', nativeLabel: 'Français', region: 'France' },
];

export default function ProfilePage({ tours = [], onReplayTour }: ProfilePageProps) {
  const { language, setLanguage, t } = useI18n();
  const [email, setEmail] = useState('');
  const [userId, setUserId] = useState('');
  const [isSavingLanguage, setIsSavingLanguage] = useState(false);
  const [languageMessageKey, setLanguageMessageKey] = useState('');
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  useEffect(() => {
    void authService.getCurrentUser().then(({ data }) => {
      setEmail(data.user?.email ?? '');
      setUserId(data.user?.id ?? '');
    });
  }, []);

  const chooseLanguage = async (nextLanguage: Language) => {
    if (isSavingLanguage) return;

    setLanguage(nextLanguage);
    setIsSavingLanguage(true);
    setLanguageMessageKey('');

    let targetUserId = userId;
    if (!targetUserId) {
      const { data } = await authService.getCurrentUser();
      targetUserId = data.user?.id ?? '';
      setUserId(targetUserId);
    }

    if (!targetUserId) {
      setLanguageMessageKey('Saved on this device. Cloud sync is temporarily unavailable.');
      setIsSavingLanguage(false);
      return;
    }

    const { synced } = await languagePreferenceService.savePreference(targetUserId, nextLanguage);
    setLanguageMessageKey(synced
      ? 'Language preference saved.'
      : 'Saved on this device. Cloud sync is temporarily unavailable.');
    setIsSavingLanguage(false);
  };

  const handleLogout = async () => {
    setIsLoggingOut(true);
    try {
      await authService.signOut();
    } finally {
      setIsLoggingOut(false);
    }
  };

  return (
    <div className="page settings-page">
      <header className="settings-page__header">
        <div>
          <p className="settings-page__eyebrow">{t('Account & preferences')}</p>
          <h1 className="page__title">{t('Settings')}</h1>
          <p className="settings-page__intro">
            {t('Manage how your workspace feels and review your signed-in account.')}
          </p>
        </div>
        <div className="settings-page__status">
          <span className="settings-page__status-dot" />
          {t('Secure session active')}
        </div>
      </header>

      <div className="settings-page__layout">
        <main className="settings-page__main">
          <section className="settings-panel" aria-labelledby="language-settings-title">
            <div className="settings-panel__heading">
              <span className="settings-panel__icon"><FiGlobe aria-hidden="true" /></span>
              <div>
                <h2 id="language-settings-title">{t('Language & region')}</h2>
                <p>{t('Choose the language used across your admin workspace.')}</p>
              </div>
            </div>

            <div className="settings-language-list">
              {languages.map((option) => {
                const isSelected = language === option.value;
                return (
                  <button
                    key={option.value}
                    type="button"
                    className={`settings-language ${isSelected ? 'settings-language--selected' : ''}`}
                    onClick={() => { void chooseLanguage(option.value); }}
                    disabled={isSavingLanguage}
                    aria-pressed={isSelected}
                  >
                    <span className="settings-language__code">{option.value.toUpperCase()}</span>
                    <span className="settings-language__copy">
                      <strong>{option.nativeLabel}</strong>
                      <small>{t(option.region)}</small>
                    </span>
                    <span className="settings-language__selected" aria-hidden="true">
                      {isSelected && <FiCheck />}
                    </span>
                  </button>
                );
              })}
            </div>
            <div className="settings-panel__footer">
              <p className="settings-panel__help">
                {t('Your choice is saved to your account and follows you across devices.')}
              </p>
              {languageMessageKey && <p className="settings-panel__saved">{t(languageMessageKey)}</p>}
            </div>
          </section>

          {onReplayTour && (
            <section className="settings-panel settings-panel--tour" aria-labelledby="tour-settings-title">
              <div className="settings-panel__heading">
                <span className="settings-panel__icon settings-panel__icon--accent"><FiPlayCircle aria-hidden="true" /></span>
                <div>
                  <h2 id="tour-settings-title">{t('Guided tour')}</h2>
                  <p>{t('Choose a tour to revisit the parts of the portal you need.')}</p>
                </div>
              </div>
              <div className="settings-tour-list">
                {tours.map((tour) => (
                  <div className="settings-tour-list__item" key={tour.id}>
                    <span><strong>{t(tour.name)}</strong><small>{t(tour.description)}</small></span>
                    <button type="button" className="settings-page__secondary-button" onClick={() => onReplayTour(tour.id)}>
                      <FiPlayCircle aria-hidden="true" />
                      {tour.id === 'site' ? t('Replay site tour') : t('Replay tour')}
                    </button>
                  </div>
                ))}
              </div>
            </section>
          )}

          <section className="settings-panel settings-panel--danger" aria-labelledby="session-settings-title">
            <div className="settings-panel__heading">
              <span className="settings-panel__icon settings-panel__icon--danger"><FiLogOut aria-hidden="true" /></span>
              <div>
                <h2 id="session-settings-title">{t('Session')}</h2>
                <p>{t('Sign out securely when you have finished using this device.')}</p>
              </div>
            </div>
            <button
              type="button"
              className="settings-page__logout-button"
              onClick={() => { void handleLogout(); }}
              disabled={isLoggingOut}
            >
              <FiLogOut aria-hidden="true" />
              {isLoggingOut ? t('Signing out...') : t('Sign out')}
            </button>
          </section>
        </main>

        <aside className="settings-account-card">
          <div className="settings-account-card__shield"><FiShield aria-hidden="true" /></div>
          <p className="settings-account-card__eyebrow">{t('Signed in as')}</p>
          <h2>{t('Administrator')}</h2>
          <div className="settings-account-card__email">
            <FiMail aria-hidden="true" />
            <span>{email || t('Loading...')}</span>
          </div>
          <div className="settings-account-card__divider" />
          <div className="settings-account-card__detail">
            <span>{t('Access')}</span>
            <strong>{t('Admin workspace')}</strong>
          </div>
          <div className="settings-account-card__detail">
            <span>{t('Current language')}</span>
            <strong>{language === 'fr' ? 'Français' : 'English'}</strong>
          </div>
        </aside>
      </div>
    </div>
  );
}
